import type { ChordShape } from './types';

/**
 * Fretted chord shapes.
 *
 * Fret arrays are ordered like the tuning: lowest-numbered string first. For a
 * ukulele in GCEA that is G C E A; for a guitar it is E A D G B e. `null` mutes
 * the string.
 *
 * Every shape is checked against chord theory by `verifyShapes()` and by the
 * test suite: the pitch classes a shape actually produces must equal the pitch
 * classes of the chord it claims to be. A typo here fails the build rather than
 * teaching someone the wrong grip.
 */

const uke = (
  symbol: string,
  frets: (number | null)[],
  tier: number,
  extra: Partial<ChordShape> = {},
): ChordShape => ({
  id: `uke.${symbol.toLowerCase().replace(/[^a-z0-9]/g, '')}${extra.id ?? ''}`,
  symbol,
  instrument: 'ukulele',
  frets,
  tier,
  ...extra,
});

/** Ukulele, standard GCEA tuning. Strings ordered G C E A. */
export const UKULELE_SHAPES: ChordShape[] = [
  // Tier 1: the first four chords, all open.
  uke('C', [0, 0, 0, 3], 1, { fingers: [null, null, null, 3], tags: ['open'] }),
  uke('Am', [2, 0, 0, 0], 1, { fingers: [2, null, null, null], tags: ['open'] }),
  uke('F', [2, 0, 1, 0], 1, { fingers: [2, null, 1, null], tags: ['open'] }),
  uke('G7', [0, 2, 1, 2], 1, { fingers: [null, 2, 1, 3], tags: ['open'] }),

  // Tier 2: rounds out the first-position chords.
  uke('G', [0, 2, 3, 2], 2, { fingers: [null, 1, 3, 2], tags: ['open'] }),
  uke('C7', [0, 0, 0, 1], 2, { fingers: [null, null, null, 1], tags: ['open'] }),
  uke('A7', [0, 1, 0, 0], 2, { fingers: [null, 1, null, null], tags: ['open'] }),
  uke('D7', [2, 2, 2, 3], 2, { fingers: [1, 1, 1, 2] }),
  uke('Dm', [2, 2, 1, 0], 2, { fingers: [2, 3, 1, null] }),
  uke('Em', [0, 4, 3, 2], 2, { fingers: [null, 4, 3, 2] }),
  uke('A', [2, 1, 0, 0], 2, { fingers: [2, 1, null, null], tags: ['open'] }),

  // Tier 3: more shapes, first barre.
  uke('D', [2, 2, 2, 0], 3, { fingers: [1, 2, 3, null] }),
  uke('E7', [1, 2, 0, 2], 3, { fingers: [1, 2, null, 3] }),
  uke('B7', [2, 3, 2, 2], 3, { fingers: [1, 3, 1, 1] }),
  uke('Bb', [3, 2, 1, 1], 3, {
    fingers: [3, 2, 1, 1],
    barre: { fret: 1, from: 1, to: 2 },
    tags: ['barre'],
  }),
  uke('E', [4, 4, 4, 2], 3, { fingers: [3, 3, 3, 1] }),

  // Tier 4: sevenths and color chords.
  uke('Fmaj7', [2, 4, 1, 3], 4, { fingers: [2, 4, 1, 3] }),
  uke('Cmaj7', [0, 0, 0, 2], 4, { fingers: [null, null, null, 2] }),
  uke('Am7', [0, 0, 0, 0], 4, { tags: ['open'] }),
  uke('Em7', [0, 2, 0, 2], 4, { fingers: [null, 1, null, 2] }),
  uke('Dm7', [2, 2, 1, 3], 4, { fingers: [2, 3, 1, 4] }),
  uke('Gm', [0, 2, 3, 1], 4, { fingers: [null, 2, 3, 1] }),
  uke('Fm', [1, 0, 1, 3], 4, { fingers: [1, null, 2, 4] }),
  uke('Csus4', [0, 0, 1, 3], 4, { fingers: [null, null, 1, 3] }),
  uke('Gsus4', [0, 2, 3, 3], 4, { fingers: [null, 1, 2, 3] }),

  // Tier 5: barre families.
  uke('Bm', [4, 2, 2, 2], 5, {
    fingers: [3, 1, 1, 1],
    barre: { fret: 2, from: 1, to: 3 },
    tags: ['barre'],
  }),
  uke('B', [4, 3, 2, 2], 5, {
    fingers: [4, 3, 1, 1],
    barre: { fret: 2, from: 1, to: 2 },
    tags: ['barre'],
  }),
  uke('Bbm', [3, 1, 1, 1], 5, {
    fingers: [3, 1, 1, 1],
    barre: { fret: 1, from: 1, to: 3 },
    tags: ['barre'],
  }),
  uke('F#m', [2, 1, 2, 0], 5, { fingers: [2, 1, 3, null] }),
  uke('C#m', [1, 1, 0, 4], 5, { fingers: [1, 1, null, 4] }),
  uke('Eb', [0, 3, 3, 1], 5, { fingers: [null, 2, 3, 1] }),
  uke('Ab', [5, 3, 4, 3], 5, { fingers: [4, 1, 3, 2] }),
  uke('Db', [1, 1, 1, 4], 5, {
    fingers: [1, 1, 1, 4],
    barre: { fret: 1, from: 1, to: 3 },
    tags: ['barre'],
  }),
  uke('F#', [3, 1, 2, 1], 5, { fingers: [3, 1, 2, 1] }),
  uke('G#m', [4, 3, 4, 2], 5, { fingers: [3, 2, 4, 1] }),
  uke('Ebm', [3, 3, 2, 1], 5, { fingers: [3, 4, 2, 1] }),

  // Tier 6: jazz voicings.
  uke('Gmaj7', [0, 2, 2, 2], 6, { fingers: [null, 1, 1, 1] }),
  uke('Dmaj7', [2, 2, 2, 4], 6, { fingers: [1, 1, 1, 3] }),
  uke('Amaj7', [1, 1, 0, 0], 6, { fingers: [1, 2, null, null] }),
  uke('Cm', [0, 3, 3, 3], 6, { fingers: [null, 1, 1, 1] }),
  uke('Cm7', [3, 3, 3, 3], 6, {
    fingers: [1, 1, 1, 1],
    barre: { fret: 3, from: 1, to: 4 },
    tags: ['barre'],
  }),
  uke('Gm7', [0, 2, 1, 1], 6, { fingers: [null, 3, 1, 1] }),
  uke('Bm7', [2, 2, 2, 2], 6, {
    fingers: [1, 1, 1, 1],
    barre: { fret: 2, from: 1, to: 4 },
    tags: ['barre'],
  }),
  uke('F7', [2, 3, 1, 3], 6, { fingers: [2, 3, 1, 4] }),
  uke('Bb7', [1, 2, 1, 1], 6, { fingers: [1, 2, 1, 1] }),
  uke('Eb7', [3, 3, 3, 4], 6, { fingers: [1, 1, 1, 2] }),
  uke('Ab7', [1, 3, 2, 3], 6, { fingers: [1, 3, 2, 4] }),

  /*
   * Tier 6, movable. The ukulele's own shape system (plan doc 07): four barre
   * shapes that keep their intervals as they slide up the neck, so a player who
   * knows these four can play every major, minor, seventh and minor-seventh
   * chord without learning a new grip.
   *
   * Named for the chord each makes at the nut: the A shape barred at fret 1 is
   * B♭, at fret 3 is C, and so on. Every one of them frets all four strings,
   * which is what makes it movable — an open string cannot move.
   */
  uke('Bb', [3, 2, 1, 1], 6, {
    id: '.shape-a',
    fingers: [4, 3, 1, 1],
    barre: { fret: 1, from: 3, to: 4 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  uke('Bbm', [3, 1, 1, 1], 6, {
    id: '.shape-am',
    fingers: [3, 1, 1, 1],
    barre: { fret: 1, from: 2, to: 4 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  uke('Bb7', [1, 2, 1, 1], 6, {
    id: '.shape-a7',
    fingers: [1, 2, 1, 1],
    barre: { fret: 1, from: 1, to: 4 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  uke('Bbm7', [1, 1, 1, 1], 6, {
    id: '.shape-am7',
    fingers: [1, 1, 1, 1],
    barre: { fret: 1, from: 1, to: 4 },
    tags: ['barre', 'movable'],
    movable: true,
  }),

  // Tier 7: extensions and symmetric chords.
  uke('C6', [0, 0, 0, 0], 7, { tags: ['open'] }),
  uke('Dm6', [2, 2, 1, 2], 7, { fingers: [2, 3, 1, 4] }),
  uke('Cdim7', [2, 3, 2, 3], 7, { fingers: [1, 3, 2, 4] }),
  uke('Bdim7', [1, 2, 1, 2], 7, { fingers: [1, 3, 2, 4] }),
  uke('Caug', [1, 0, 0, 3], 7, { fingers: [1, null, null, 4] }),
  uke('Csus2', [0, 2, 3, 3], 7, { fingers: [null, 1, 2, 3] }),
];

const gtr = (
  symbol: string,
  frets: (number | null)[],
  tier: number,
  extra: Partial<ChordShape> = {},
): ChordShape => ({
  id: `gtr.${symbol.toLowerCase().replace(/[^a-z0-9]/g, '')}${extra.id ?? ''}`,
  symbol,
  instrument: 'guitar',
  frets,
  tier,
  ...extra,
});

/** Guitar, standard tuning. Strings ordered E A D G B e. */
export const GUITAR_SHAPES: ChordShape[] = [
  gtr('Em', [0, 2, 2, 0, 0, 0], 1, { fingers: [null, 2, 3, null, null, null], tags: ['open'] }),
  gtr('Am', [null, 0, 2, 2, 1, 0], 1, { fingers: [null, null, 2, 3, 1, null], tags: ['open'] }),
  gtr('D', [null, null, 0, 2, 3, 2], 1, { fingers: [null, null, null, 1, 3, 2], tags: ['open'] }),
  gtr('E', [0, 2, 2, 1, 0, 0], 2, { fingers: [null, 2, 3, 1, null, null], tags: ['open'] }),
  gtr('A', [null, 0, 2, 2, 2, 0], 2, { fingers: [null, null, 1, 2, 3, null], tags: ['open'] }),
  gtr('G', [3, 2, 0, 0, 0, 3], 2, { fingers: [3, 2, null, null, null, 4], tags: ['open'] }),
  gtr('C', [null, 3, 2, 0, 1, 0], 2, { fingers: [null, 3, 2, null, 1, null], tags: ['open'] }),
  gtr('Dm', [null, null, 0, 2, 3, 1], 2, { fingers: [null, null, null, 2, 3, 1], tags: ['open'] }),
  gtr('E7', [0, 2, 0, 1, 0, 0], 3, { fingers: [null, 2, null, 1, null, null], tags: ['open'] }),
  gtr('A7', [null, 0, 2, 0, 2, 0], 3, { fingers: [null, null, 2, null, 3, null], tags: ['open'] }),
  gtr('D7', [null, null, 0, 2, 1, 2], 3, { fingers: [null, null, null, 2, 1, 3], tags: ['open'] }),
  gtr('G7', [3, 2, 0, 0, 0, 1], 3, { fingers: [3, 2, null, null, null, 1], tags: ['open'] }),
  gtr('C7', [null, 3, 2, 3, 1, 0], 3, { fingers: [null, 3, 2, 4, 1, null], tags: ['open'] }),
  gtr('Cadd9', [null, 3, 2, 0, 3, 3], 4, { fingers: [null, 2, 1, null, 3, 4] }),
  gtr('Asus2', [null, 0, 2, 2, 0, 0], 4, { fingers: [null, null, 1, 2, null, null] }),
  gtr('Dsus4', [null, null, 0, 2, 3, 3], 4, { fingers: [null, null, null, 1, 3, 4] }),
  gtr('Esus4', [0, 2, 2, 2, 0, 0], 4, { fingers: [null, 1, 2, 3, null, null] }),
  gtr('Cmaj7', [null, 3, 2, 0, 0, 0], 4, { fingers: [null, 3, 2, null, null, null] }),
  gtr('Am7', [null, 0, 2, 0, 1, 0], 4, { fingers: [null, null, 2, null, 1, null] }),
  gtr('Dm7', [null, null, 0, 2, 1, 1], 4, { fingers: [null, null, null, 2, 1, 1] }),
  gtr('Gmaj7', [3, 2, 0, 0, 0, 2], 4, { fingers: [3, 1, null, null, null, 2] }),
  gtr('Dmaj7', [null, null, 0, 2, 2, 2], 4, { fingers: [null, null, null, 1, 1, 1] }),
  gtr('Amaj7', [null, 0, 2, 1, 2, 0], 4, { fingers: [null, null, 2, 1, 3, null] }),
  gtr('E5', [0, 2, 2, null, null, null], 4, {
    fingers: [null, 1, 2, null, null, null],
    tags: ['power'],
  }),
  gtr('A5', [null, 0, 2, 2, null, null], 4, {
    fingers: [null, null, 1, 2, null, null],
    tags: ['power'],
  }),
  gtr('D5', [null, null, 0, 2, 3, null], 4, {
    fingers: [null, null, null, 1, 3, null],
    tags: ['power'],
  }),

  // Tier 5: barre chords.
  gtr('F', [1, 3, 3, 2, 1, 1], 5, {
    fingers: [1, 3, 4, 2, 1, 1],
    barre: { fret: 1, from: 1, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('Bb', [null, 1, 3, 3, 3, 1], 5, {
    fingers: [null, 1, 2, 3, 4, 1],
    barre: { fret: 1, from: 2, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('Bm', [null, 2, 4, 4, 3, 2], 5, {
    fingers: [null, 1, 3, 4, 2, 1],
    barre: { fret: 2, from: 2, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('Bbm', [null, 1, 3, 3, 2, 1], 5, {
    fingers: [null, 1, 3, 4, 2, 1],
    barre: { fret: 1, from: 2, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('F#m', [2, 4, 4, 2, 2, 2], 5, {
    fingers: [1, 3, 4, 1, 1, 1],
    barre: { fret: 2, from: 1, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  // Gm and Cm: the Am shape barred at the tenth and third frets. A song in
  // D minor or F major needs one of them, and the audit found the gap.
  gtr('Gm', [3, 5, 5, 3, 3, 3], 5, {
    fingers: [1, 3, 4, 1, 1, 1],
    barre: { fret: 3, from: 1, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('Cm', [null, 3, 5, 5, 4, 3], 5, {
    fingers: [null, 1, 3, 4, 2, 1],
    barre: { fret: 3, from: 2, to: 6 },
    tags: ['barre', 'movable'],
    movable: true,
  }),
  gtr('B7', [null, 2, 1, 2, 0, 2], 5, { fingers: [null, 2, 1, 3, null, 4] }),
  gtr('Em7', [0, 2, 2, 0, 3, 0], 5, { fingers: [null, 2, 3, null, 4, null] }),
];

export const ALL_SHAPES: ChordShape[] = [...UKULELE_SHAPES, ...GUITAR_SHAPES];

const BY_ID = new Map(ALL_SHAPES.map((s) => [s.id, s]));

export function getShape(id: string): ChordShape {
  const shape = BY_ID.get(id);
  if (shape === undefined) throw new Error(`Unknown chord shape: ${id}`);
  return shape;
}

/** All shapes for a chord symbol on an instrument, easiest tier first. */
export function shapesFor(
  symbol: string,
  instrument: 'ukulele' | 'guitar',
  options: { maxTier?: number } = {},
): ChordShape[] {
  const maxTier = options.maxTier ?? Infinity;
  const wanted = asciiSymbol(symbol);
  return ALL_SHAPES.filter(
    (s) => s.instrument === instrument && asciiSymbol(s.symbol) === wanted && s.tier <= maxTier,
  ).sort((a, b) => a.tier - b.tier);
}

/** The library spells flats and sharps in ASCII; theory formats them as glyphs. */
function asciiSymbol(symbol: string): string {
  return symbol.replace(/♭/g, 'b').replace(/♯/g, '#');
}

/** Chord symbols playable on an instrument within a tier limit. */
export function symbolsAvailable(instrument: 'ukulele' | 'guitar', maxTier: number): string[] {
  return [
    ...new Set(
      ALL_SHAPES.filter((s) => s.instrument === instrument && s.tier <= maxTier).map(
        (s) => s.symbol,
      ),
    ),
  ];
}
